/**
 * Coach IA d'entretien d'embauche - Groupe AERH Polynesie
 * Adapté à la charte AERH (teal/gold, fonds clairs)
 * Backend : /api/coach/chat + /api/coach/extract-job (Emergent LLM Key)
 * Voice : navigateur (SpeechRecognition + speechSynthesis)
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_AI_Q = 10;

// Palette AERH
const C = {
  bg: "#FAFAF9", surf: "#FFFFFF", card: "#FFFFFF", border: "#E5E7EB",
  pri: "#0F766E", priD: "#115E59", priL: "#14B8A6",
  amb: "#F59E0B", ambL: "#FBBF24",
  blue: "#3B82F6", red: "#EF4444",
  white: "#FFFFFF", text: "#1F2937", muted: "#64748B",
  ok: "#10B981", warn: "#F59E0B", bad: "#EF4444", star: "#F59E0B",
  surfDark: "#F1F5F9",
};

const TOP20 = [
  { id: 1,  cat: "Introduction",        q: "Pouvez-vous vous présenter en quelques mots ?" },
  { id: 2,  cat: "Motivation",          q: "Pourquoi postulez-vous à ce poste ?" },
  { id: 3,  cat: "Motivation",          q: "Qu'est-ce qui vous a attiré vers notre entreprise ?" },
  { id: 4,  cat: "Parcours",            q: "Pourquoi souhaitez-vous quitter votre poste actuel ?" },
  { id: 5,  cat: "Qualités",            q: "Quelles sont vos trois principales qualités professionnelles ?" },
  { id: 6,  cat: "Axe d'amélioration",  q: "Quel est selon vous votre principal point d'amélioration ?" },
  { id: 7,  cat: "Projection",          q: "Où vous voyez-vous professionnellement dans 5 ans ?" },
  { id: 8,  cat: "Réussite",            q: "Décrivez une réussite professionnelle dont vous êtes fier(e)." },
  { id: 9,  cat: "Échec",               q: "Parlez-moi d'une difficulté rencontrée et comment vous l'avez surmontée." },
  { id: 10, cat: "Stress",              q: "Comment gérez-vous le stress et les situations de pression ?" },
  { id: 11, cat: "Valeur ajoutée",      q: "Pourquoi devrions-nous vous choisir plutôt qu'un autre candidat ?" },
  { id: 12, cat: "Rémunération",        q: "Quelles sont vos prétentions salariales ?" },
  { id: 13, cat: "Disponibilité",       q: "Êtes-vous disponible immédiatement ? Quel est votre préavis ?" },
  { id: 14, cat: "Relationnel",         q: "Comment vos collègues ou manager vous décriraient-ils ?" },
  { id: 15, cat: "Organisation",        q: "Comment organisez-vous vos priorités au quotidien ?" },
  { id: 16, cat: "Gestion conflits",    q: "Décrivez une situation de conflit et comment vous l'avez gérée." },
  { id: 17, cat: "Adaptabilité",        q: "Comment vous adaptez-vous au changement et aux imprévus ?" },
  { id: 18, cat: "Motivation",          q: "Qu'est-ce qui vous motive le plus dans votre travail ?" },
  { id: 19, cat: "Travail d'équipe",    q: "Préférez-vous travailler seul(e) ou en équipe ? Donnez un exemple." },
  { id: 20, cat: "Questions candidat",  q: "Avez-vous des questions à nous poser ?" },
];

// ── Voice Hook ────────────────────────────────────────────
function useVoice({ onFinal, onInterim }) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSupported(true);
    const r = new SR();
    r.lang = "fr-FR";
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (e) => {
      let fin = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) fin += t; else interim += t;
      }
      if (fin) onFinal(fin);
      onInterim(interim);
    };
    r.onend = () => { setListening(false); onInterim(""); };
    r.onerror = () => { setListening(false); onInterim(""); };
    recRef.current = r;
  }, [onFinal, onInterim]);

  const startListen = () => {
    if (!recRef.current || listening) return;
    try { recRef.current.start(); setListening(true); } catch { /* noop */ }
  };
  const stopListen = () => {
    if (!recRef.current || !listening) return;
    try { recRef.current.stop(); } catch { /* noop */ }
    setListening(false); onInterim("");
  };
  const toggleListen = () => listening ? stopListen() : startListen();

  const speak = useCallback((text, { onEnd } = {}) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-FR"; u.rate = 0.95; u.pitch = 1.05;
    u.onstart = () => setSpeaking(true);
    u.onend = () => { setSpeaking(false); if (onEnd) onEnd(); };
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
  }, []);

  const stopSpeak = () => { window.speechSynthesis?.cancel(); setSpeaking(false); };

  return { listening, supported, speaking, startListen, stopListen, toggleListen, speak, stopSpeak };
}

// ── Mini composants UI ────────────────────────────────────
function VoiceWave({ active, color = C.pri }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 3, height: 24 }}>
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} style={{
          width: 3, borderRadius: 2, background: color,
          height: active ? undefined : 6,
          animation: active ? `coachWave 1s ease-in-out ${i * 0.1}s infinite` : "none",
          transition: "height 0.2s",
        }} />
      ))}
    </div>
  );
}

function MicButton({ listening, supported, onClick, size = 44 }) {
  if (!supported) return null;
  return (
    <button onClick={onClick} data-testid="coach-mic-btn"
      style={{
        width: size, height: size, borderRadius: "50%", cursor: "pointer",
        background: listening ? C.red : C.surf,
        border: `1px solid ${listening ? C.red : C.border}`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        transition: "all 0.2s",
        animation: listening ? "coachMicPulse 1.4s ease-in-out infinite" : "none",
        boxShadow: listening ? `0 0 0 0 ${C.red}60` : "0 1px 3px rgba(0,0,0,0.06)",
      }}
      title={listening ? "Arrêter l'enregistrement" : "Répondre à voix haute"}>
      {listening
        ? <svg width="18" height="18" viewBox="0 0 24 24" fill={C.white}><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect x="9" y="2" width="6" height="12" rx="3" stroke={C.pri} strokeWidth="2" />
          <path d="M5 10a7 7 0 0014 0M12 19v3M9 22h6" stroke={C.pri} strokeWidth="2" strokeLinecap="round" />
        </svg>
      }
    </button>
  );
}

function TTSButton({ speaking, enabled, onToggleEnabled, onStop, size = 36 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {speaking && (
        <button onClick={onStop} data-testid="coach-tts-stop"
          style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontSize: 11, cursor: "pointer" }}>
          Stop
        </button>
      )}
      <button onClick={onToggleEnabled} data-testid="coach-tts-toggle"
        title={enabled ? "Désactiver la lecture des questions" : "Lire les questions à voix haute"}
        style={{
          width: size, height: size, borderRadius: "50%", border: `1px solid ${enabled ? C.blue : C.border}`,
          background: enabled ? C.blue + "15" : C.surf, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s",
        }}>
        {enabled
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" stroke={C.blue} strokeWidth="2" strokeLinecap="round" />
          </svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" stroke={C.muted} strokeWidth="2" strokeLinecap="round" />
          </svg>
        }
      </button>
    </div>
  );
}

function Stars({ count, max = 5 }) {
  return <span>{Array.from({ length: max }).map((_, i) => <span key={i} style={{ color: i < count ? C.star : C.border, fontSize: 18 }}>★</span>)}</span>;
}

function Pill({ children, color = C.pri }) {
  return <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20, background: color + "18", border: `1px solid ${color}33`, color, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>{children}</span>;
}

function BarScore({ score, label }) {
  const col = score >= 75 ? C.ok : score >= 50 ? C.warn : C.bad;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: C.text }}>{label}</span>
        <span style={{ fontSize: 12, color: col, fontWeight: 700 }}>{score}/100</span>
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${score}%`, background: `linear-gradient(90deg,${col},${col}aa)`, borderRadius: 3, transition: "width 1s ease" }} />
      </div>
    </div>
  );
}

function ProgressRing({ value, size = 100 }) {
  const r = (size - 14) / 2; const circ = 2 * Math.PI * r; const dash = (value / 100) * circ;
  const col = value >= 75 ? C.ok : value >= 50 ? C.warn : C.bad;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.border} strokeWidth={10} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={10} strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" style={{ transition: "stroke-dasharray 1.4s ease-out" }} />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="middle" fill={col} fontSize={size / 3.8} fontWeight="800" style={{ transform: `rotate(90deg)`, transformOrigin: `${size / 2}px ${size / 2}px`, fontFamily: "monospace" }}>{value}</text>
    </svg>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: "flex", gap: 5, padding: "12px 16px", alignItems: "center" }}>
      {[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.pri, animation: `coachBounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────
export default function CoachInterviewPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("welcome");
  const [job, setJob] = useState({ title: "", description: "", requirements: "", experience: "", competences: "" });
  const [setupTab, setSetupTab] = useState("manual");
  const [trainingMode, setTrainingMode] = useState(null);
  const [candidateName, setCandidateName] = useState("");
  const [selectedQs, setSelectedQs] = useState(TOP20.map(q => q.id));
  const [difficulty, setDifficulty] = useState("standard");
  const [ttsEnabled, setTtsEnabled] = useState(true);

  const [aiMessages, setAiMessages] = useState([]);
  const [aiQCount, setAiQCount] = useState(0);
  const [aiInput, setAiInput] = useState("");

  const [q20Index, setQ20Index] = useState(0);
  const [q20Answers, setQ20Answers] = useState([]);
  const [q20Input, setQ20Input] = useState("");
  const [q20Feedback, setQ20Feedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadFileName, setUploadFileName] = useState("");
  const [report, setReport] = useState(null);
  const [genStep, setGenStep] = useState("");
  const [interimText, setInterimText] = useState("");

  // Email envoi du bilan
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const sessionIdRef = useRef(null);

  const currentInput = phase === "training-ai" ? aiInput : q20Input;
  const setCurrentInput = phase === "training-ai" ? setAiInput : setQ20Input;

  const onFinal = useCallback((text) => {
    setCurrentInput(prev => (prev ? prev + " " : "") + text);
    setInterimText("");
  }, [setCurrentInput]);
  const onInterim = useCallback((text) => setInterimText(text), []);
  const voice = useVoice({ onFinal, onInterim });

  const lastAiMsg = aiMessages.length > 0 ? aiMessages[aiMessages.length - 1] : null;
  const lastAiMsgRef = useRef(null);
  useEffect(() => {
    if (!ttsEnabled || !lastAiMsg || lastAiMsg.role !== "assistant") return;
    if (lastAiMsg.content === lastAiMsgRef.current) return;
    lastAiMsgRef.current = lastAiMsg.content;
    setTimeout(() => voice.speak(lastAiMsg.content), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAiMsg, ttsEnabled]);

  useEffect(() => {
    if (!ttsEnabled || phase !== "training-20q") return;
    const q = TOP20.filter(q => selectedQs.includes(q.id))[q20Index];
    if (q) setTimeout(() => voice.speak(q.q), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q20Index, phase, ttsEnabled]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [aiMessages, loading, showFeedback]);

  // ── API helpers ─────────────────────────────────────────
  const callChat = async (system, messages, maxTokens = 1000) => {
    const r = await axios.post(`${API}/api/coach/chat`, {
      system, messages, max_tokens: maxTokens,
      session_id: sessionIdRef.current,
    });
    if (!sessionIdRef.current && r.data.session_id) sessionIdRef.current = r.data.session_id;
    return r.data.text;
  };

  // ── File upload ─────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    setUploadLoading(true); setUploadError(""); setUploadFileName(file.name);
    try {
      let payload;
      if (file.type === "text/plain") {
        const txt = await file.text();
        payload = { text: txt };
      } else {
        const b64 = await new Promise((ok, err) => {
          const r = new FileReader();
          r.onload = e => ok(e.target.result.split(",")[1]);
          r.onerror = () => err(new Error("read fail"));
          r.readAsDataURL(file);
        });
        payload = { file_base64: b64, file_mime: file.type || "application/pdf" };
      }
      const r = await axios.post(`${API}/api/coach/extract-job`, payload);
      setJob(r.data);
      setSetupTab("manual");
    } catch (e) {
      console.error(e);
      setUploadError("Extraction impossible. Vérifiez le fichier ou saisissez manuellement.");
    }
    setUploadLoading(false);
  };
  const onFileDrop = useCallback(e => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (f) handleFile(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Job context & prompts ───────────────────────────────
  const jobCtx = () => !job.title ? "Entretien d'embauche général"
    : `Poste : ${job.title}${job.description ? `\nMissions : ${job.description}` : ""}${job.requirements ? `\nCompétences : ${job.requirements}` : ""}${job.experience ? `\nExpérience : ${job.experience}` : ""}`;

  const aiSys = (n) => {
    const diff = { doux: "Ton bienveillant, questions douces.", standard: "Ton professionnel et structuré.", intensif: "Ton exigeant et challengeant." }[difficulty];
    return `Tu es Alex, coach expert en entretiens d'embauche. Tu joues le rôle d'un recruteur pour entraîner ${candidateName || "le candidat"}.
CONTEXTE : ${jobCtx()}
MODE : ${diff}  PROGRESSION : ${n + 1}/${MAX_AI_Q}
${n === 0 ? `Accueille chaleureusement par le prénom, présente-toi brièvement, puis pose la question de présentation/parcours.`
        : n === MAX_AI_Q - 1 ? `Dernière question. Pose une question sur ce que le candidat souhaiterait demander au recruteur.`
          : `Pose la prochaine question en t'appuyant sur les réponses précédentes. Varie les types : comportementale (STAR), technique, motivation, projection.`}
RÈGLES : une seule question concise, formulée comme un vrai recruteur, sans commenter les réponses.
Réponds UNIQUEMENT avec ta question.`;
  };

  // ── AI mode ─────────────────────────────────────────────
  const startAI = async () => {
    sessionIdRef.current = null;
    setPhase("training-ai"); setLoading(true); setAiMessages([]); setAiQCount(0); setAiInput(""); setInterimText("");
    try {
      const res = await callChat(aiSys(0), [{ role: "user", content: "[L'entretien commence]" }]);
      setAiMessages([{ role: "assistant", content: res }]); setAiQCount(1);
    } catch (e) { console.error(e); }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 200);
  };

  const sendAI = async () => {
    if (!aiInput.trim() || loading) return;
    voice.stopListen(); voice.stopSpeak();
    const msg = { role: "user", content: aiInput.trim() };
    const updated = [...aiMessages, msg];
    setAiMessages(updated); setAiInput(""); setInterimText(""); setLoading(true);
    try {
      if (aiQCount >= MAX_AI_Q) {
        const closing = await callChat(`Tu es Alex, coach. L'entraînement avec ${candidateName || "le candidat"} est terminé. Clôture chaleureusement en 2 phrases : remercie-le, annonce le rapport.`, updated);
        const final = [...updated, { role: "assistant", content: closing }];
        setAiMessages(final); setLoading(false);
        setTimeout(() => generateReport("ai", final, []), 2500);
      } else {
        const res = await callChat(aiSys(aiQCount), updated);
        setAiMessages([...updated, { role: "assistant", content: res }]);
        setAiQCount(c => c + 1); setLoading(false);
      }
    } catch (e) { console.error(e); setLoading(false); }
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ── Top20 mode ──────────────────────────────────────────
  const activeQs = TOP20.filter(q => selectedQs.includes(q.id));

  const startQ20 = () => {
    setPhase("training-20q"); setQ20Index(0); setQ20Answers([]); setQ20Input(""); setInterimText("");
    setQ20Feedback(null); setShowFeedback(false);
  };

  const submitQ20 = async () => {
    if (!q20Input.trim() || loading) return;
    voice.stopListen(); voice.stopSpeak();
    const question = activeQs[q20Index].q, answer = q20Input.trim();
    setLoading(true); setQ20Input(""); setInterimText("");
    try {
      const prompt = `Tu es un coach expert en entretiens. Analyse cette réponse de ${candidateName || "le candidat"}.
CONTEXTE : ${jobCtx()}
QUESTION : "${question}"
RÉPONSE : "${answer}"
Réponds UNIQUEMENT en JSON (sans markdown) :
{"stars":<1-5>,"point_fort":"<1 phrase>","amelioration":"<1 axe concret>","conseil":"<conseil technique précis>","reformulation":"<début de réponse améliorée, 1-2 phrases>"}`;
      const res = await callChat("", [{ role: "user", content: prompt }]);
      const cleaned = res.replace(/```json|```/g, "").trim();
      const start = cleaned.indexOf("{"); const end = cleaned.lastIndexOf("}");
      const fb = JSON.parse(start >= 0 ? cleaned.slice(start, end + 1) : cleaned);
      setQ20Feedback(fb); setShowFeedback(true);
      setQ20Answers(prev => [...prev, { question, answer, feedback: fb }]);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const nextQ20 = () => {
    voice.stopSpeak(); setShowFeedback(false); setQ20Feedback(null); setQ20Input(""); setInterimText("");
    if (q20Index + 1 >= activeQs.length) generateReport("top20", [], q20Answers);
    else { setQ20Index(i => i + 1); setTimeout(() => inputRef.current?.focus(), 100); }
  };

  // ── Report ──────────────────────────────────────────────
  const generateReport = async (mode, aiMsgs, q20Data) => {
    voice.stopSpeak(); voice.stopListen();
    setPhase("generating");
    for (const s of ["Analyse de vos réponses…", "Identification des points forts…", "Calcul du score…", "Rédaction du rapport…"]) {
      setGenStep(s); await new Promise(r => setTimeout(r, 850));
    }
    try {
      const transcript = mode === "ai"
        ? aiMsgs.map(m => `${m.role === "assistant" ? "Coach" : (candidateName || "Candidat")} : ${m.content}`).join("\n\n")
        : q20Data.map((a, i) => `Q${i + 1} [${activeQs[i]?.cat}]: ${a.question}\nRéponse: ${a.answer}`).join("\n\n");
      const prompt = `Tu es un coach expert. Génère un bilan d'entraînement encourageant.
CANDIDAT : ${candidateName || "Candidat"}
${jobCtx()}
MODE : ${mode === "ai" ? "Simulation IA adaptative" : `Top ${activeQs.length} questions`}
${transcript}
Réponds UNIQUEMENT en JSON valide (sans markdown) :
{"score_global":<0-100>,"niveau":"Débutant"|"Intermédiaire"|"Confirmé"|"Expert","message_encouragement":"<2 phrases motivantes>","score_clarte":<0-100>,"score_contenu":<0-100>,"score_structure":<0-100>,"score_impact":<0-100>,"points_forts":["<f1>","<f2>","<f3>"],"axes_progres":["<a1>","<a2>","<a3>"],"top5_conseils":["<c1>","<c2>","<c3>","<c4>","<c5>"],"phrases_a_eviter":["<e1>","<e2>"],"phrases_cles":["<p1>","<p2>","<p3>"],"prochaine_etape":"<action concrète>"}`;
      const res = await callChat("", [{ role: "user", content: prompt }], 2000);
      const cleaned = res.replace(/```json|```/g, "").trim();
      const start = cleaned.indexOf("{"); const end = cleaned.lastIndexOf("}");
      setReport(JSON.parse(start >= 0 ? cleaned.slice(start, end + 1) : cleaned));
      setPhase("report");
    } catch (e) { console.error(e); setPhase(mode === "ai" ? "training-ai" : "training-20q"); }
  };

  // ── Envoi du bilan par email ────────────────────────────
  const sendReportEmail = async () => {
    if (!emailValue.trim() || !/.+@.+\..+/.test(emailValue)) {
      setEmailError("Veuillez saisir une adresse email valide.");
      return;
    }
    setEmailSending(true); setEmailError("");
    try {
      await axios.post(`${API}/api/coach/send-report`, {
        email: emailValue.trim(),
        candidate_name: candidateName,
        job_title: job.title || "",
        mode: trainingMode,
        report,
      });
      setEmailSent(true);
    } catch (e) {
      console.error(e);
      setEmailError(e?.response?.data?.detail || "Échec de l'envoi. Réessayez plus tard.");
    }
    setEmailSending(false);
  };

  const closeEmailModal = () => {
    setEmailModalOpen(false);
    setTimeout(() => { setEmailValue(""); setEmailSent(false); setEmailError(""); }, 200);
  };

  // ── Styles ──────────────────────────────────────────────
  const CSS = `
    .coach-page,.coach-page *,.coach-page *::before,.coach-page *::after{box-sizing:border-box;}
    .coach-page textarea{resize:none;font-family:inherit;}
    .coach-page button{cursor:pointer;transition:all 0.18s;font-family:inherit;}
    .coach-page button:hover{opacity:0.92;}
    .coach-page input:focus,.coach-page textarea:focus{outline:none;border-color:${C.pri}!important;box-shadow:0 0 0 3px ${C.pri}25;}
    .coach-page ::-webkit-scrollbar{width:6px;}
    .coach-page ::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px;}
    @keyframes coachSpin{to{transform:rotate(360deg)}}
    @keyframes coachBounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}
    @keyframes coachFadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
    @keyframes coachGlow{0%,100%{box-shadow:0 6px 18px ${C.pri}25}50%{box-shadow:0 8px 24px ${C.pri}40}}
    @keyframes coachPulse{0%,100%{opacity:1}50%{opacity:0.45}}
    @keyframes coachWave{0%,100%{height:4px}50%{height:20px}}
    @keyframes coachMicPulse{0%,100%{box-shadow:0 0 0 0 ${C.red}50}70%{box-shadow:0 0 0 10px ${C.red}00}}
    .coach-anim{animation:coachFadeUp 0.4s ease both;}
    .coach-btn-pri{background:linear-gradient(135deg,${C.pri},${C.priD});color:#fff;border:none;border-radius:12px;padding:13px 28px;font-size:14px;font-weight:700;letter-spacing:0.02em;}
    .coach-btn-ghost{background:transparent;color:${C.muted};border:1px solid ${C.border};border-radius:10px;padding:11px 22px;font-size:13px;}
    .coach-btn-ghost:hover{border-color:${C.pri};color:${C.pri};}
    .coach-btn-amb{background:linear-gradient(135deg,${C.amb},${C.ambL});color:#fff;border:none;border-radius:12px;padding:13px 28px;font-size:14px;font-weight:700;}
    .coach-card{background:${C.card};border:1px solid ${C.border};border-radius:16px;box-shadow:0 1px 3px rgba(0,0,0,0.04);}
    .coach-inp{background:${C.surf};border:1px solid ${C.border};border-radius:10px;padding:12px 16px;color:${C.text};font-size:14px;width:100%;font-family:inherit;}
    .coach-label{font-size:11px;font-weight:700;color:${C.muted};letter-spacing:0.1em;text-transform:uppercase;display:block;margin-bottom:6px;}
    .coach-tab-active{background:${C.pri}18!important;border-color:${C.pri}55!important;color:${C.pri}!important;}
  `;

  const shell = { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "inherit" };
  const center = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "calc(100vh - 80px)", padding: "24px" };

  // ── VoiceInputZone (shared) ─────────────────────────────
  const VoiceInputZone = ({ value, onChange, onSend, placeholder, sendColor = "coach-btn-pri", disabled = false }) => (
    <div>
      {interimText && (
        <div style={{ marginBottom: 8, padding: "8px 14px", background: C.pri + "0E", border: `1px solid ${C.pri}22`, borderRadius: 10, fontSize: 13, color: C.pri, fontStyle: "italic", display: "flex", alignItems: "center", gap: 8 }}>
          <VoiceWave active={true} color={C.pri} />
          <span style={{ opacity: 0.85 }}>{interimText}</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
        <MicButton listening={voice.listening} supported={voice.supported} onClick={voice.toggleListen} size={46} />
        <textarea ref={inputRef} className="coach-inp" data-testid="coach-input"
          style={{ minHeight: 52, maxHeight: 140, flex: 1, paddingTop: 14, lineHeight: 1.55 }}
          placeholder={voice.supported ? `Parlez ou écrivez ici… (${placeholder})` : placeholder}
          value={value} onChange={e => onChange(e.target.value)} disabled={disabled || loading}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }} />
        <button onClick={onSend} disabled={loading || !value.trim() || disabled} data-testid="coach-send"
          className={sendColor} style={{ padding: "13px 16px", opacity: (loading || !value.trim() || disabled) ? 0.4 : 1, borderRadius: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {voice.listening
            ? <><VoiceWave active={true} color={C.red} /><span style={{ fontSize: 11, color: C.red, fontWeight: 700 }}>Enregistrement… (cliquez sur Stop pour arrêter)</span></>
            : voice.supported
              ? <span style={{ fontSize: 11, color: C.muted }}>Cliquez sur le micro pour répondre à voix haute</span>
              : <span style={{ fontSize: 11, color: C.muted }}>Saisie clavier uniquement (ce navigateur ne supporte pas le micro)</span>
          }
        </div>
        <TTSButton speaking={voice.speaking} enabled={ttsEnabled}
          onToggleEnabled={() => { setTtsEnabled(v => !v); if (voice.speaking) voice.stopSpeak(); }}
          onStop={voice.stopSpeak} />
      </div>
      <div style={{ fontSize: 10, color: C.muted, marginTop: 4, textAlign: "center" }}>
        {voice.supported && "Astuce : Structurez vos réponses STAR (Situation → Tâche → Action → Résultat)"}
      </div>
    </div>
  );

  // ── PHASES ──────────────────────────────────────────────
  // WELCOME
  if (phase === "welcome") return (
    <div className="coach-page" style={shell}>
      <style>{CSS}</style>
      <div style={{ ...center, gap: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }} className="coach-anim">
          <div style={{ width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg,${C.pri},${C.priL})`, display: "flex", alignItems: "center", justifyContent: "center", animation: "coachGlow 3s ease-in-out infinite" }}>
            <svg width="30" height="30" fill="none" viewBox="0 0 24 24"><path stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" d="M8 9h8M8 13h6M7 3H4a1 1 0 00-1 1v16a1 1 0 001 1h16a1 1 0 001-1V4a1 1 0 00-1-1h-3M7 3a2 2 0 014 0h2a2 2 0 014 0M7 3a2 2 0 000 4h10a2 2 0 000-4" /></svg>
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: "-0.02em" }}>Coach <span style={{ color: C.pri }}>IA</span> d'entretien</div>
            <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.12em", textTransform: "uppercase" }}>Groupe AERH Polynésie</div>
          </div>
        </div>

        <div style={{ textAlign: "center", maxWidth: 600, marginBottom: 40 }} className="coach-anim">
          <h1 style={{ fontSize: "clamp(28px,5vw,46px)", fontWeight: 800, color: C.text, lineHeight: 1.12, letterSpacing: "-0.03em", marginBottom: 14 }}>
            Entraînez-vous à voix haute.<br />
            <span style={{ backgroundImage: `linear-gradient(90deg,${C.pri},${C.amb})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Progressez avec l'IA.
            </span>
          </h1>
          <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.7 }}>
            Simulez un vrai entretien adapté à votre poste. Répondez <strong style={{ color: C.text }}>à voix haute ou par écrit</strong>. Obtenez un coaching personnalisé sur chaque réponse.
          </p>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center", marginBottom: 40 }} className="coach-anim">
          {[
            { t: "Réponses vocales", s: "Parlez directement au micro" },
            { t: "Questions lues", s: "Le recruteur IA parle" },
            { t: "Import offre", s: "PDF, image ou texte" },
            { t: "Coaching temps réel", s: "Feedback sur chaque réponse" },
          ].map(f => (
            <div key={f.t} className="coach-card" style={{ padding: "16px 20px", minWidth: 160, maxWidth: 190 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: C.pri + "15", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.pri }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>{f.t}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{f.s}</div>
            </div>
          ))}
        </div>

        <button className="coach-btn-pri coach-anim" data-testid="coach-start-btn"
          style={{ fontSize: 16, padding: "16px 52px", borderRadius: 14 }} onClick={() => setPhase("job-setup")}>
          Commencer mon entraînement →
        </button>
        <p style={{ marginTop: 14, fontSize: 12, color: C.muted }}>Gratuit · Sans inscription · 100 % confidentiel</p>

        <button className="coach-btn-ghost" style={{ marginTop: 20, fontSize: 12 }} onClick={() => navigate("/recrutement")}>
          ← Retour au site
        </button>
      </div>
    </div>
  );

  // JOB SETUP
  if (phase === "job-setup") return (
    <div className="coach-page" style={shell}>
      <style>{CSS}</style>
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "36px 20px" }}>
        <button className="coach-btn-ghost" style={{ marginBottom: 24, fontSize: 13 }} onClick={() => setPhase("welcome")}>← Retour</button>
        <div className="coach-anim" style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 6 }}>Votre poste cible</h2>
          <p style={{ fontSize: 14, color: C.muted }}>Renseignez le poste ou importez l'offre — plus c'est précis, plus les questions seront pertinentes. <span style={{ color: C.pri }}>Vous pouvez aussi laisser vide pour un entraînement général.</span></p>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 20, background: C.surfDark, padding: 5, borderRadius: 12, width: "fit-content" }}>
          {[["manual", "Saisie manuelle"], ["upload", "Importer l'offre"]].map(([k, l]) => (
            <button key={k} className={`coach-btn-ghost ${setupTab === k ? "coach-tab-active" : ""}`}
              style={{ padding: "8px 20px", fontSize: 13, fontWeight: 600 }} onClick={() => setSetupTab(k)}>{l}</button>
          ))}
        </div>

        {setupTab === "upload" && (
          <div className="coach-anim coach-card" style={{ padding: 28, marginBottom: 20 }}>
            <div onDrop={onFileDrop} onDragOver={e => e.preventDefault()} onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${C.border}`, borderRadius: 12, padding: "36px 20px", textAlign: "center", cursor: "pointer", background: C.surfDark }}>
              <input ref={fileRef} type="file" accept=".pdf,.txt,.png,.jpg,.jpeg,.webp" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
              {uploadLoading
                ? <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 36, height: 36, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.pri}`, borderRadius: "50%", animation: "coachSpin 0.8s linear infinite" }} />
                  <div style={{ fontSize: 14, color: C.pri }}>Extraction en cours…</div>
                </div>
                : <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>{uploadFileName ? `✓ ${uploadFileName}` : "Glissez votre offre ici"}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>ou cliquez · PDF, image JPG/PNG, fichier texte</div>
                  {uploadError && <div style={{ marginTop: 12, color: C.bad, fontSize: 13 }}>⚠ {uploadError}</div>}
                  {uploadFileName && !uploadLoading && <div style={{ marginTop: 12, color: C.pri, fontSize: 13 }}>Vérifiez dans l'onglet « Saisie manuelle »</div>}
                </>
              }
            </div>
          </div>
        )}

        {setupTab === "manual" && (
          <div className="coach-anim coach-card" style={{ padding: 28 }}>
            <div style={{ display: "grid", gap: 16 }}>
              <div><label className="coach-label">Intitulé du poste</label>
                <input className="coach-inp" placeholder="Ex : Chargé(e) RH, Développeur Full Stack…" value={job.title} onChange={e => setJob(j => ({ ...j, title: e.target.value }))} data-testid="coach-job-title" /></div>
              <div><label className="coach-label">Missions principales</label>
                <textarea className="coach-inp" style={{ minHeight: 80 }} placeholder="Décrivez les missions…" value={job.description} onChange={e => setJob(j => ({ ...j, description: e.target.value }))} /></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div><label className="coach-label">Compétences techniques</label>
                  <textarea className="coach-inp" style={{ minHeight: 70 }} placeholder="Ex : Excel, droit social…" value={job.requirements} onChange={e => setJob(j => ({ ...j, requirements: e.target.value }))} /></div>
                <div><label className="coach-label">Compétences comportementales</label>
                  <textarea className="coach-inp" style={{ minHeight: 70 }} placeholder="Ex : leadership, rigueur…" value={job.competences} onChange={e => setJob(j => ({ ...j, competences: e.target.value }))} /></div>
              </div>
              <div><label className="coach-label">Expérience souhaitée</label>
                <input className="coach-inp" placeholder="Ex : 3-5 ans en cabinet RH…" value={job.experience} onChange={e => setJob(j => ({ ...j, experience: e.target.value }))} /></div>
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 16 }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Exemples rapides :</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { l: "RH Généraliste", d: { title: "Chargé(e) RH Généraliste", description: "Recrutement, formation, paie, relations sociales.", requirements: "Paie, droit du travail, recrutement, SIRH", experience: "3-5 ans", competences: "Rigueur, polyvalence, communication" } },
                  { l: "Conducteur travaux", d: { title: "Conducteur de Travaux", description: "Coordination et suivi de chantiers, management terrain.", requirements: "Lecture de plans, chiffrage, gestion chantiers", experience: "5 ans+", competences: "Leadership, organisation, réactivité" } },
                  { l: "Commercial B2B", d: { title: "Commercial(e) B2B", description: "Développement et fidélisation d'un portefeuille clients.", requirements: "CRM, négociation, prospection", experience: "2-4 ans", competences: "Persuasion, écoute active, résilience" } },
                ].map(ex => (
                  <button key={ex.l} className="coach-btn-ghost" style={{ padding: "5px 14px", fontSize: 12 }} onClick={() => setJob(ex.d)}>{ex.l}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="coach-anim coach-card" style={{ padding: 20, marginTop: 16 }}>
          <label className="coach-label" style={{ marginBottom: 10 }}>Niveau de difficulté</label>
          <div style={{ display: "flex", gap: 10 }}>
            {[["doux", "Doux", "Bienveillant"], ["standard", "Standard", "Classique"], ["intensif", "Intensif", "Exigeant"]].map(([k, l, d]) => (
              <button key={k} onClick={() => setDifficulty(k)} data-testid={`coach-diff-${k}`}
                style={{
                  flex: 1, padding: "10px 8px", borderRadius: 10, border: `1px solid ${difficulty === k ? C.pri : C.border}`,
                  background: difficulty === k ? C.pri + "15" : C.surf, color: difficulty === k ? C.pri : C.muted,
                  fontSize: 13, fontWeight: 600, textAlign: "center"
                }}>
                <div>{l}</div><div style={{ fontSize: 10, marginTop: 3, color: difficulty === k ? C.priL : C.muted }}>{d}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
          <button className="coach-btn-ghost" onClick={() => setPhase("welcome")}>Annuler</button>
          <button className="coach-btn-pri" onClick={() => setPhase("mode-select")} data-testid="coach-next-mode">Choisir le mode →</button>
        </div>
      </div>
    </div>
  );

  // MODE SELECT
  if (phase === "mode-select") return (
    <div className="coach-page" style={shell}>
      <style>{CSS}</style>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "36px 20px" }}>
        <button className="coach-btn-ghost" style={{ marginBottom: 24, fontSize: 13 }} onClick={() => setPhase("job-setup")}>← Retour</button>
        {job.title && <div style={{ marginBottom: 20, padding: "10px 16px", background: C.pri + "12", border: `1px solid ${C.pri}33`, borderRadius: 10, fontSize: 13, color: C.pri }}>Poste : <strong>{job.title}</strong></div>}
        <h2 style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 6 }} className="coach-anim">Mode d'entraînement</h2>
        <p style={{ fontSize: 14, color: C.muted, marginBottom: 32 }}>Les deux modes supportent les réponses vocales et la lecture des questions.</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }} className="coach-anim">
          {[
            { k: "ai", title: "Entretien IA adaptatif", desc: `L'IA adapte ses questions à VOS réponses. L'expérience la plus réaliste.`, feats: ["Questions adaptatives", "Recruteur IA vocal", `${MAX_AI_Q} questions`, "Rapport final"], col: C.pri },
            { k: "top20", title: "Top 20 questions classiques", desc: "Travaillez les 20 questions incontournables avec coaching immédiat.", feats: ["Feedback après chaque réponse", "Conseils méthode STAR", "Sélection des questions", "Rapport final"], col: C.amb },
          ].map(m => (
            <div key={m.k} onClick={() => setTrainingMode(m.k)} data-testid={`coach-mode-${m.k}`}
              style={{
                background: trainingMode === m.k ? m.col + "10" : C.card,
                border: `2px solid ${trainingMode === m.k ? m.col : C.border}`,
                borderRadius: 16, padding: 24, cursor: "pointer", transition: "all 0.2s",
                transform: trainingMode === m.k ? "translateY(-2px)" : "none",
                boxShadow: trainingMode === m.k ? `0 8px 24px ${m.col}25` : "0 1px 3px rgba(0,0,0,0.04)"
              }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: m.col + "20", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: m.col }} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 8 }}>{m.title}</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>{m.desc}</div>
              {m.feats.map(f => <div key={f} style={{ fontSize: 12, color: trainingMode === m.k ? m.col : C.muted, marginBottom: 4 }}>✓ {f}</div>)}
              {trainingMode === m.k && <div style={{ marginTop: 12 }}><Pill color={m.col}>Sélectionné</Pill></div>}
            </div>
          ))}
        </div>

        <div className="coach-anim coach-card" style={{ padding: 18, marginBottom: 20, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: C.blue + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" stroke={C.blue} strokeWidth="2" strokeLinecap="round" /></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>Paramètres vocaux</div>
            <div style={{ fontSize: 12, color: C.muted }}>{voice.supported ? "Micro détecté — réponses vocales disponibles" : "Ce navigateur ne supporte pas le micro (Chrome recommandé)"}</div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <div style={{ position: "relative", width: 42, height: 24 }}>
              <input type="checkbox" checked={ttsEnabled} onChange={e => setTtsEnabled(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: ttsEnabled ? C.blue : C.border, borderRadius: 12, transition: "0.3s" }} />
              <div style={{ position: "absolute", top: 2, left: ttsEnabled ? 20 : 2, width: 20, height: 20, background: C.white, borderRadius: "50%", transition: "0.3s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
            </div>
            <span style={{ fontSize: 12, color: C.muted }}>Questions lues à voix haute</span>
          </label>
        </div>

        {trainingMode === "top20" && (
          <div className="coach-anim coach-card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <label className="coach-label" style={{ marginBottom: 0 }}>Questions ({selectedQs.length}/20)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="coach-btn-ghost" style={{ padding: "5px 12px", fontSize: 11 }} onClick={() => setSelectedQs(TOP20.map(q => q.id))}>Tout</button>
                <button className="coach-btn-ghost" style={{ padding: "5px 12px", fontSize: 11 }} onClick={() => setSelectedQs([])}>Aucun</button>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto", paddingRight: 4 }}>
              {TOP20.map(q => (
                <label key={q.id} style={{
                  display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "8px 12px",
                  background: selectedQs.includes(q.id) ? C.pri + "0E" : C.surf, borderRadius: 8,
                  border: `1px solid ${selectedQs.includes(q.id) ? C.pri + "44" : C.border}`, transition: "all 0.15s"
                }}>
                  <input type="checkbox" checked={selectedQs.includes(q.id)} style={{ marginTop: 2, accentColor: C.pri }}
                    onChange={e => setSelectedQs(prev => e.target.checked ? [...prev, q.id] : prev.filter(id => id !== q.id))} />
                  <div>
                    <div style={{ fontSize: 10, color: selectedQs.includes(q.id) ? C.pri : C.muted, fontWeight: 700, marginBottom: 2 }}>{q.cat}</div>
                    <div style={{ fontSize: 12, color: C.text, lineHeight: 1.4 }}>{q.q}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="coach-anim coach-card" style={{ padding: 18, marginBottom: 20 }}>
          <label className="coach-label" style={{ marginBottom: 8 }}>Votre prénom (optionnel)</label>
          <input className="coach-inp" placeholder="Pour un coaching personnalisé…" value={candidateName} onChange={e => setCandidateName(e.target.value)} data-testid="coach-name" />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button className="coach-btn-ghost" onClick={() => setPhase("job-setup")}>Retour</button>
          <button className={trainingMode === "top20" ? "coach-btn-amb" : "coach-btn-pri"}
            disabled={!trainingMode || (trainingMode === "top20" && selectedQs.length === 0)}
            data-testid="coach-start-training"
            style={{ opacity: (!trainingMode || (trainingMode === "top20" && selectedQs.length === 0)) ? 0.4 : 1 }}
            onClick={() => trainingMode === "ai" ? startAI() : startQ20()}>
            {trainingMode === "top20" ? "Démarrer les questions" : "Démarrer l'entretien IA"}
          </button>
        </div>
      </div>
    </div>
  );

  // TRAINING — AI MODE
  if (phase === "training-ai") return (
    <div className="coach-page" style={{ ...shell, display: "flex", flexDirection: "column", height: "100vh" }}>
      <style>{CSS}</style>
      <div style={{ background: C.surf, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: `linear-gradient(135deg,${C.pri},${C.priL})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800 }}>A</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
            Alex — Coach recruteur IA
            {voice.speaking && <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.blue }}><VoiceWave active={true} color={C.blue} /> en train de parler</span>}
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>{job.title || "Entretien général"} · {difficulty === "doux" ? "Doux" : difficulty === "intensif" ? "Intensif" : "Standard"}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <TTSButton speaking={voice.speaking} enabled={ttsEnabled} onToggleEnabled={() => { setTtsEnabled(v => !v); if (voice.speaking) voice.stopSpeak(); }} onStop={voice.stopSpeak} />
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Q {Math.min(aiQCount, MAX_AI_Q)} / {MAX_AI_Q}</div>
            <div style={{ width: 120, height: 4, background: C.border, borderRadius: 2 }}>
              <div style={{ height: "100%", width: `${Math.min(aiQCount / MAX_AI_Q, 1) * 100}%`, background: `linear-gradient(90deg,${C.pri},${C.priL})`, borderRadius: 2, transition: "width 0.5s" }} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px", display: "flex", flexDirection: "column", gap: 14, background: C.bg }}>
        {aiMessages.map((m, i) => (
          <div key={i} style={{ display: "flex", gap: 10, justifyContent: m.role === "user" ? "flex-end" : "flex-start", animation: "coachFadeUp 0.3s ease" }}>
            {m.role === "assistant" && <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg,${C.pri},${C.priL})`, color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13 }}>A</div>}
            <div style={{
              maxWidth: "78%", padding: "12px 16px",
              borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
              background: m.role === "user" ? `linear-gradient(135deg,${C.pri},${C.priD})` : C.card,
              border: m.role === "user" ? "none" : `1px solid ${C.border}`,
              fontSize: 14, lineHeight: 1.65, color: m.role === "user" ? C.white : C.text
            }}>
              {m.content}
            </div>
            {m.role === "user" && <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.amb + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: C.amb, flexShrink: 0 }}>{candidateName ? candidateName[0].toUpperCase() : "?"}</div>}
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 10, animation: "coachFadeUp 0.3s ease" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg,${C.pri},${C.priL})`, color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>A</div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "18px 18px 18px 4px" }}><TypingIndicator /></div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 16px", background: C.surf, flexShrink: 0 }}>
        <VoiceInputZone value={aiInput} onChange={setAiInput} onSend={sendAI} placeholder="Entrée = envoyer · Maj+Entrée = saut de ligne" />
      </div>
    </div>
  );

  // TRAINING — TOP 20 MODE
  if (phase === "training-20q") {
    const totalQ = activeQs.length;
    const qData = activeQs[q20Index];
    const progPct = totalQ > 0 ? Math.round((q20Index / totalQ) * 100) : 0;
    if (!qData) return null;

    return (
      <div className="coach-page" style={shell}>
        <style>{CSS}</style>
        <div style={{ background: C.surf, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.amb + "22", display: "flex", alignItems: "center", justifyContent: "center", color: C.amb, fontWeight: 800 }}>20</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
              Top {totalQ} questions classiques
              {voice.speaking && <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.blue }}><VoiceWave active={true} color={C.blue} /> lecture en cours</span>}
            </div>
            <div style={{ fontSize: 11, color: C.muted }}>{job.title || "Entretien général"}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <TTSButton speaking={voice.speaking} enabled={ttsEnabled} onToggleEnabled={() => { setTtsEnabled(v => !v); if (voice.speaking) voice.stopSpeak(); }} onStop={voice.stopSpeak} />
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Q {q20Index + 1} / {totalQ}</div>
              <div style={{ width: 120, height: 4, background: C.border, borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${progPct}%`, background: `linear-gradient(90deg,${C.amb},${C.ambL})`, borderRadius: 2, transition: "width 0.5s" }} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 20px" }}>
          <div className="coach-anim coach-card" style={{ padding: 28, marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.amb + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: C.amb, flexShrink: 0 }}>{q20Index + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.amb, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>{qData.cat}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1.4 }}>{qData.q}</div>
              </div>
              {ttsEnabled && (
                <button onClick={() => voice.speak(qData.q)} title="Réécouter la question"
                  style={{ width: 34, height: 34, borderRadius: "50%", border: `1px solid ${C.border}`, background: C.surf, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 010 7.07" stroke={C.blue} strokeWidth="2" strokeLinecap="round" /></svg>
                </button>
              )}
            </div>
            <div style={{ padding: "10px 14px", background: C.surfDark, borderRadius: 9, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              {q20Index === 0 ? "Conseil : présentez-vous en 2-3 min : parcours → compétences clés → motivation pour ce poste."
                : q20Index === 7 || q20Index === 8 ? "Conseil : méthode STAR — Situation, Tâche, Action, Résultat."
                  : q20Index === 4 || q20Index === 5 ? "Conseil : soyez précis et donnez un exemple concret."
                    : q20Index === 10 ? "Conseil : citez 2-3 atouts spécifiques et différenciants."
                      : q20Index === 11 ? "Conseil : donnez une fourchette basée sur votre marché."
                        : "Conseil : soyez honnête, précis et illustrez avec des exemples concrets."}
            </div>
          </div>

          {!showFeedback ? (
            <div className="coach-anim">
              <VoiceInputZone value={q20Input} onChange={setQ20Input} onSend={submitQ20}
                placeholder="Entrée (clavier) ou parlez directement"
                sendColor="coach-btn-amb" disabled={loading} />
              {loading && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 16, gap: 10, alignItems: "center" }}>
                  <div style={{ width: 18, height: 18, border: `2px solid ${C.border}`, borderTop: `2px solid ${C.pri}`, borderRadius: "50%", animation: "coachSpin 0.8s linear infinite" }} />
                  <span style={{ fontSize: 13, color: C.muted }}>L'IA analyse votre réponse…</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button className="coach-btn-ghost" style={{ fontSize: 12, padding: "7px 16px" }} onClick={nextQ20}>Passer cette question →</button>
              </div>
            </div>
          ) : q20Feedback && (
            <div className="coach-anim coach-card" style={{ padding: 24, border: `1px solid ${C.pri}55` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Coaching IA</div>
                <Stars count={q20Feedback.stars} />
              </div>
              <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
                {[
                  { col: C.ok, label: "Point fort", val: q20Feedback.point_fort },
                  { col: C.warn, label: "À améliorer", val: q20Feedback.amelioration },
                  { col: C.blue, label: "Conseil pro", val: q20Feedback.conseil },
                  q20Feedback.reformulation && { col: C.pri, label: "Exemple de reformulation", val: `« ${q20Feedback.reformulation} »` },
                ].filter(Boolean).map((item, i) => (
                  <div key={i} style={{ padding: "11px 15px", background: item.col + "0E", border: `1px solid ${item.col}30`, borderRadius: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: item.col, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5, fontStyle: i === 3 ? "italic" : "normal" }}>{item.val}</div>
                  </div>
                ))}
              </div>
              <button className="coach-btn-pri" style={{ width: "100%" }} onClick={nextQ20} data-testid="coach-next-q">
                {q20Index + 1 < totalQ ? `Question suivante (${q20Index + 2}/${totalQ}) →` : "Voir mon bilan final →"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // GENERATING
  if (phase === "generating") return (
    <div className="coach-page" style={{ ...shell, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <style>{CSS}</style>
      <div style={{ textAlign: "center", animation: "coachFadeUp 0.4s ease" }}>
        <div style={{ width: 70, height: 70, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.pri}`, borderRadius: "50%", margin: "0 auto 24px", animation: "coachSpin 1s linear infinite" }} />
        <h3 style={{ fontSize: 24, fontWeight: 800, color: C.text, marginBottom: 8 }}>Génération de votre bilan…</h3>
        <div style={{ fontSize: 14, color: C.pri, animation: "coachPulse 1.8s ease-in-out infinite" }}>{genStep}</div>
      </div>
    </div>
  );

  // REPORT
  if (phase === "report" && report) {
    const lvlColor = { Débutant: C.warn, Intermédiaire: C.blue, Confirmé: C.pri, Expert: C.amb }[report.niveau] || C.pri;
    return (
      <div className="coach-page" style={shell}>
        <style>{CSS}</style>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "32px 20px" }}>
          <div className="coach-anim coach-card" style={{ padding: 28, marginBottom: 18, position: "relative", overflow: "hidden", background: `linear-gradient(135deg,${C.surf},${C.surfDark})` }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: C.pri + "08" }} />
            <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap", position: "relative" }}>
              <ProgressRing value={report.score_global} size={100} />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  <Pill color={lvlColor}>{report.niveau}</Pill>
                  <Pill color={C.pri}>{trainingMode === "ai" ? "Entretien IA" : `Top ${activeQs.length} questions`}</Pill>
                  {job.title && <Pill color={C.muted}>{job.title}</Pill>}
                </div>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: C.text, marginBottom: 8, letterSpacing: "-0.02em" }}>
                  {candidateName ? `Bilan de ${candidateName}` : "Votre bilan d'entraînement"}
                </h2>
                <p style={{ fontSize: 14, color: C.text, lineHeight: 1.7, borderLeft: `3px solid ${C.pri}`, paddingLeft: 12 }}>{report.message_encouragement}</p>
              </div>
            </div>
          </div>

          <div className="coach-anim coach-card" style={{ padding: 24, marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>Grille de performance</div>
            <BarScore score={report.score_clarte} label="Clarté et communication" />
            <BarScore score={report.score_contenu} label="Qualité et pertinence du contenu" />
            <BarScore score={report.score_structure} label="Structure des réponses" />
            <BarScore score={report.score_impact} label="Impact et conviction" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }} className="coach-anim">
            <div className="coach-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.ok, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>Vos points forts</div>
              {report.points_forts.map((p, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10, fontSize: 13, color: C.text, lineHeight: 1.5 }}><span style={{ color: C.ok, flexShrink: 0 }}>▸</span>{p}</div>)}
            </div>
            <div className="coach-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.warn, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>Axes de progression</div>
              {report.axes_progres.map((p, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10, fontSize: 13, color: C.text, lineHeight: 1.5 }}><span style={{ color: C.warn, flexShrink: 0 }}>▸</span>{p}</div>)}
            </div>
          </div>

          <div className="coach-anim coach-card" style={{ padding: 24, marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>5 conseils prioritaires pour le vrai entretien</div>
            {report.top5_conseils.map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 12, marginBottom: 12, padding: "12px 14px", background: C.surfDark, borderRadius: 10, borderLeft: `3px solid ${C.pri}` }}>
                <span style={{ fontWeight: 800, color: C.pri, fontSize: 15, flexShrink: 0, fontFamily: "monospace" }}>{String(i + 1).padStart(2, "0")}</span>
                <span style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{c}</span>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }} className="coach-anim">
            <div className="coach-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.bad, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>À éviter</div>
              {report.phrases_a_eviter.map((p, i) => <div key={i} style={{ padding: "8px 12px", background: C.bad + "0E", border: `1px solid ${C.bad}22`, borderRadius: 8, marginBottom: 8, fontSize: 12, color: C.text, fontStyle: "italic" }}>« {p} »</div>)}
            </div>
            <div className="coach-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.ok, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Formulations clés</div>
              {report.phrases_cles.map((p, i) => <div key={i} style={{ padding: "8px 12px", background: C.ok + "0E", border: `1px solid ${C.ok}22`, borderRadius: 8, marginBottom: 8, fontSize: 12, color: C.text, fontStyle: "italic" }}>« {p}… »</div>)}
            </div>
          </div>

          <div className="coach-anim coach-card" style={{ padding: 20, marginBottom: 24, borderLeft: `4px solid ${C.pri}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Prochaine étape recommandée</div>
            <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7 }}>{report.prochaine_etape}</div>
          </div>

          {/* CTA email — recevoir le bilan */}
          <div className="coach-anim coach-card" style={{ padding: 22, marginBottom: 18, background: `linear-gradient(135deg,${C.pri}0E,${C.amb}0E)`, border: `1px solid ${C.pri}33` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: C.pri + "1F", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6" stroke={C.pri} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Conservez votre bilan</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.55 }}>
                  Recevez ce rapport complet par email pour le relire avant votre vrai entretien et suivre votre progression.
                </div>
              </div>
              <button className="coach-btn-pri" data-testid="coach-send-report-btn"
                onClick={() => setEmailModalOpen(true)}
                style={{ padding: "12px 22px", fontSize: 14, flexShrink: 0 }}>
                Recevoir mon bilan par email
              </button>
            </div>
          </div>

          <div className="coach-anim" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="coach-btn-pri" data-testid="coach-restart" onClick={() => {
              setCandidateName(""); setAiMessages([]); setAiQCount(0); setQ20Index(0); setQ20Answers([]);
              setQ20Feedback(null); setShowFeedback(false); setReport(null); setPhase("mode-select");
            }}>Recommencer</button>
            <button className="coach-btn-ghost" onClick={() => {
              setJob({ title: "", description: "", requirements: "", experience: "", competences: "" });
              setPhase("job-setup");
            }}>Changer de poste</button>
            <button className="coach-btn-ghost" onClick={() => navigate("/recrutement")}>Retour au site</button>
          </div>
        </div>

        {/* Modal email */}
        {emailModalOpen && (
          <div data-testid="coach-email-modal"
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000, animation: "coachFadeUp 0.2s ease" }}
            onClick={(e) => { if (e.target === e.currentTarget) closeEmailModal(); }}>
            <div className="coach-card" style={{ maxWidth: 460, width: "100%", padding: 28, background: C.surf, animation: "coachFadeUp 0.3s ease" }}>
              {!emailSent ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: C.pri + "1F", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6" stroke={C.pri} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Recevoir mon bilan</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Le rapport complet sera envoyé sur votre email.</div>
                    </div>
                  </div>
                  <label className="coach-label" style={{ marginBottom: 6 }}>Votre adresse email</label>
                  <input type="email" className="coach-inp" data-testid="coach-email-input"
                    placeholder="prenom.nom@exemple.com"
                    value={emailValue} onChange={e => { setEmailValue(e.target.value); setEmailError(""); }}
                    onKeyDown={e => { if (e.key === "Enter" && !emailSending) sendReportEmail(); }}
                    autoFocus disabled={emailSending} />
                  {emailError && (
                    <div style={{ marginTop: 10, padding: "8px 12px", background: C.bad + "10", border: `1px solid ${C.bad}33`, borderRadius: 8, fontSize: 12, color: C.bad }}>{emailError}</div>
                  )}
                  <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
                    <button className="coach-btn-ghost" onClick={closeEmailModal} disabled={emailSending}>Annuler</button>
                    <button className="coach-btn-pri" data-testid="coach-email-submit"
                      onClick={sendReportEmail} disabled={emailSending || !emailValue.trim()}
                      style={{ opacity: (emailSending || !emailValue.trim()) ? 0.5 : 1 }}>
                      {emailSending ? "Envoi..." : "Envoyer le bilan"}
                    </button>
                  </div>
                  <div style={{ marginTop: 14, fontSize: 11, color: C.muted, textAlign: "center", lineHeight: 1.55 }}>
                    En envoyant, vous acceptez de recevoir un email du Groupe AERH Polynésie. Aucun démarchage commercial.
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ width: 60, height: 60, borderRadius: "50%", background: C.ok + "1F", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke={C.ok} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 6 }}>Bilan envoyé !</div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>
                    Votre rapport complet est en route vers <strong style={{ color: C.text }}>{emailValue}</strong>. Pensez à vérifier vos spams si vous ne le recevez pas dans les 5 minutes.
                  </div>
                  <button className="coach-btn-pri" onClick={closeEmailModal} style={{ width: "100%" }}>Fermer</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
